import { type Activity, findActivity, findItem, findVenue, getCalling, getItem, minigameAt, type MinigameEngine, QUALITIES, type Quality, skill, type SkillId } from '@wyn/content';
import { spendBar } from '../bars.ts';
import { balance } from '../config/balance.ts';
import { assertGame } from '../errors.ts';
import { assertCan } from '../guards.ts';
import { craftThrift, masteryEvents, masteryTiers, qualityBonus, raiseTiers, type SkillXp, xpBonus } from '../mastery.ts';
import { type ActionResult, type GameEvent, type LedgerDelta, type PlayerState, SYSTEM } from '../player.ts';
import { bestAt, type MinigameEventData } from '../progress.ts';
import { skillProgress } from '../progression.ts';
import { activityXp } from '../rewards.ts';
import { createRng, type Rng, type RngState } from '../rng.ts';
import { type InputTrace, type RealtimeSim, replay, TICK_MS } from './realtime.ts';
import { type MinigameMove, REALTIME_SIMS, type RealtimeEngine, TURN_ENGINES, type TurnEngine, type TurnEngineImpl, type Ware } from './registry.ts';
import { sha256Hex } from './sha256.ts';
import { clamp, type MinigameResult, type SettledYield } from './types.ts';

/** The pointer on the player to the one game they have in progress. */
export interface ActiveMinigame {
  sessionId: string;
  venue: string;
  activity: string;
  startedAt: number;
  expiresAt: number;
}

/**
 * A minigame in progress, as the server keeps it (`SESSION#<id>`). `serverSeed` and `state` are
 * private until the end: the client sees `commitment` (its SHA-256) and the engine's public view.
 * Real-time engines are the exception: the client needs the seed to run the simulation it replays.
 */
export interface MinigameSession {
  id: string;
  playerId: string;
  venue: string;
  activity: string;
  engine: MinigameEngine;
  /** Skill level when the attempt began; engines scale with it. */
  level: number;
  serverSeed: string;
  commitment: string;
  /** Mixed into the RNG with the first move, after the commitment was shown (dice). */
  clientSeed: string | null;
  rng: RngState | null;
  state: unknown;
  moves: unknown[];
  /** Bumped on every move; a move is written only if nobody else moved first. */
  step: number;
  startedAt: number;
  /** When the last move landed (or the session opened): timed moves are checked against it. */
  lastMoveAt: number;
  expiresAt: number;
  /** The ware brought to the table (haggling), already taken from the pack. */
  ware: Ware | null;
  /** The best tool held for the skill when the attempt began (absent on older sessions). */
  tool?: ToolUse | null;
}

/** A tool in use: which one, and the bonus it gives after quality. */
export interface ToolUse {
  itemKey: string;
  quality: Quality;
  bonus: number;
}

/**
 * The best tool in a pack for a skill, or null. Its bonus, scaled by quality as gear is in a fight,
 * is added to the XP multiplier and (halved) to the quality score of graded goods.
 */
export function bestTool(pack: readonly { itemKey: string; quality: Quality; quantity: number }[], id: SkillId): ToolUse | null {
  let best: ToolUse | null = null;
  for (const l of pack) {
    const t = l.quantity > 0 ? findItem(l.itemKey)?.tool : undefined;
    if (t?.skill !== id) continue;
    const bonus = t.bonus * balance.fight.qualityMult[l.quality];
    if (!best || bonus > best.bonus) best = { itemKey: l.itemKey, quality: l.quality, bonus };
  }
  return best;
}

export type { MinigameMove, RealtimeEngine, TurnEngine, Ware } from './registry.ts';
export const isRealtime = (e: MinigameEngine): e is RealtimeEngine => e in REALTIME_SIMS;

export const activeMinigame = (p: PlayerState, now: number): ActiveMinigame | null =>
  p.minigame && p.minigame.expiresAt > now ? p.minigame : null;

/** Leaving town or landing in gaol mid-game (or mid-fight) would strand the session, so those wait until it ends. */
export function assertNotPlaying(p: PlayerState, now: number) {
  const m = activeMinigame(p, now);
  assertGame(!m, 'BLOCKED_BY_STATE', m ? `Finish or abandon your game at ${findVenue(m.venue)?.name ?? 'the venue'} first.` : '');
  assertGame(!(p.fight && p.fight.expiresAt > now), 'BLOCKED_BY_STATE', 'Finish your fight first.');
}

/** A fresh simulation for a real-time engine. The client runs it live; the server replays it. */
export function createSim(engine: RealtimeEngine, activity: Activity, level: number, seed: string): RealtimeSim {
  return (REALTIME_SIMS[engine] as (a: Activity, level: number, seed: string) => RealtimeSim)(activity, level, seed);
}

const turnEngine = (engine: TurnEngine) => TURN_ENGINES[engine] as unknown as TurnEngineImpl<Activity, unknown, MinigameMove, unknown>;

function lookup(venue: string, activity: string) {
  const v = findVenue(venue);
  assertGame(v, 'NOT_FOUND', 'No such venue.');
  const game = minigameAt(venue);
  assertGame(game, 'NOT_FOUND', `${v.name} has not opened its doors yet.`);
  const act = findActivity(venue, activity);
  assertGame(act, 'NOT_FOUND', `${v.name} offers no such thing.`);
  return { venue: v, engine: game.engine, activity: act };
}

const stakeOf = (a: Activity) => ('stake' in a ? a.stake : 0);
/** Crafts: engines whose inputs are materials used up (lockpicks and wares come back, so they are not). */
export const CRAFT_ENGINES: readonly MinigameEngine[] = ['smithing', 'brewing', 'siege', 'alchemy'];
/** Engines that need the player to bring one of their own goods to the table. */
export const NEEDS_WARE: readonly MinigameEngine[] = ['haggling'];

/** What one of a player's goods is worth: the item's base value scaled by quality. */
export function wareValue(itemKey: string, quality: Quality): number {
  return Math.max(1, Math.round((getItem(itemKey).value ?? 0) * balance.minigames.qualityValue[quality]));
}

export interface StartOutcome {
  sessionId: string;
  venue: string;
  activity: string;
  cost: { bar: string; amount: number };
  stake: number;
  /** Thrift kept the materials in the pack. */
  spared: boolean;
}

/**
 * Sit down at a venue. Checks the place, the skill level and the bar, takes the cost, any stake
 * and inputs up front (all ledgered), and opens a session with a crypto-random server seed.
 */
export function startMinigame(
  player: PlayerState,
  input: { venue: string; activity: string; skillXp: number; sessionId: string; serverSeed: string; now: number; ware?: { itemKey: string; quality: Quality }; tool?: ToolUse | null },
): ActionResult<StartOutcome> & { session: MinigameSession } {
  const { now } = input;
  assertCan(player, 'minigame');
  const { venue, engine, activity } = lookup(input.venue, input.activity);
  assertGame(venue.town === player.town, 'WRONG_PLACE', `${venue.name} is not in this town.`);
  const playing = activeMinigame(player, now);
  assertGame(!playing, 'CONFLICT', 'Finish or abandon the game you are in first.');
  assertGame(!(player.fight && player.fight.expiresAt > now), 'BLOCKED_BY_STATE', 'Finish your fight first.');
  const level = skillProgress(input.skillXp).level;
  assertGame(level >= activity.level, 'NOT_ENOUGH', `That needs ${skill(venue.skill).name} ${activity.level}.`);
  const { bar, amount } = activity.cost;
  assertGame(player.bars[bar].value >= amount, 'NOT_ENOUGH', `You need ${amount} ${bar[0]!.toUpperCase()}${bar.slice(1)}.`);
  const stake = stakeOf(activity);
  assertGame(player.purse >= stake, 'NOT_ENOUGH', `The stake is ${stake} coin and your purse holds ${player.purse}.`);

  let ware: Ware | null = null;
  if (NEEDS_WARE.includes(engine)) {
    assertGame(input.ware, 'INVALID', 'Choose one of your goods to bring to the table.');
    const item = findItem(input.ware.itemKey);
    assertGame(item && item.value, 'INVALID', 'Nobody here will pay for that.');
    ware = { itemKey: item.key, quality: input.ware.quality, value: wareValue(item.key, input.ware.quality) };
  }

  const counterparty = `NPC:${venue.key}`;
  // Thrift (the Artisan's perk, the Craft mastery) can spare a craft its materials. Rolled from the server seed.
  const spared = CRAFT_ENGINES.includes(engine) && !!activity.inputs?.length && createRng(`${input.serverSeed}:thrift`).chance(craftThrift(player));
  const ledger: LedgerDelta[] = [
    // The ware sits on the table until the deal is done; an unsold ware comes back at settlement.
    ...(ware ? [{ kind: 'item', itemKey: ware.itemKey, quality: ware.quality, delta: -1, reason: `ware:${venue.key}`, counterparty } as const] : []),
    ...(stake ? [{ kind: 'coin', account: 'purse', delta: -stake, reason: `stake:${venue.key}`, counterparty } as const] : []),
    ...(spared ? [] : (activity.inputs ?? [])).map(
      (i): LedgerDelta => ({ kind: 'item', itemKey: i.item, quality: balance.start.quality, delta: -i.quantity, reason: `minigame:${venue.key}`, counterparty: SYSTEM }),
    ),
  ];
  const expiresAt = now + balance.minigames.sessionMs;
  const p: PlayerState = {
    ...player,
    purse: player.purse - stake,
    bars: { ...player.bars, [bar]: spendBar(player.bars[bar], amount) },
    minigame: { sessionId: input.sessionId, venue: venue.key, activity: activity.key, startedAt: now, expiresAt },
  };
  const session: MinigameSession = {
    id: input.sessionId,
    playerId: player.id,
    venue: venue.key,
    activity: activity.key,
    engine,
    level,
    serverSeed: input.serverSeed,
    commitment: sha256Hex(input.serverSeed),
    clientSeed: null,
    rng: null,
    state: isRealtime(engine) ? null : turnEngine(engine).init(activity, level, input.serverSeed, { ware }),
    moves: [],
    step: 0,
    startedAt: now,
    lastMoveAt: now,
    expiresAt,
    ware,
    tool: input.tool ?? null,
  };
  const events: GameEvent[] = spared ? [{ type: 'thrift', text: `${venue.name}: thrift spares your materials this time.`, data: { venue: venue.key } }] : [];
  return { player: p, ledger, events, outcome: { sessionId: session.id, venue: venue.key, activity: activity.key, cost: activity.cost, stake, spared }, session };
}

export function assertLive(session: MinigameSession | null, playerId: string, now: number): asserts session is MinigameSession {
  assertGame(session && session.playerId === playerId, 'NOT_FOUND', 'No such game.');
  assertGame(session.expiresAt > now, 'NOT_FOUND', 'That game has gone cold.');
}

/** The client's view of a session: the engine's public state, the commitment, and the seed only where the client must simulate. */
export function sessionView(s: MinigameSession) {
  const { activity } = lookup(s.venue, s.activity);
  return {
    id: s.id,
    venue: s.venue,
    activity: s.activity,
    engine: s.engine,
    level: s.level,
    commitment: s.commitment,
    step: s.step,
    startedAt: s.startedAt,
    expiresAt: s.expiresAt,
    seed: isRealtime(s.engine) ? s.serverSeed : null,
    ware: s.ware ?? null,
    view: isRealtime(s.engine) ? null : turnEngine(s.engine).view(activity, s.state),
  };
}
export type SessionView = ReturnType<typeof sessionView>;

/**
 * Apply one move to a turn-based session. Pure: returns the next session and, once over, the result.
 * `now` lets timed moves (a lance couched at tick N) be checked against the real time since the last move.
 */
export function playMove(s: MinigameSession, move: MinigameMove, now: number, clientSeed?: string): { session: MinigameSession; result: MinigameResult | null } {
  assertGame(!isRealtime(s.engine), 'INVALID', 'That game is played live, not move by move.');
  const { activity } = lookup(s.venue, s.activity);
  const eng = turnEngine(s.engine as TurnEngine);
  assertGame(eng.result(activity, s.state) === null, 'INVALID', 'The game is over.');
  const seedOfClient = s.clientSeed ?? clientSeed ?? '';
  const rng = s.rng ? createRng(s.rng) : createRng(`${s.serverSeed}:${seedOfClient}`);
  const elapsedMs = now - (s.lastMoveAt ?? s.startedAt);
  const state = eng.move(activity, s.state, move, { rng, elapsedMs, level: s.level, ware: s.ware ?? null });
  const session: MinigameSession = {
    ...s,
    state,
    rng: rng.state(),
    clientSeed: seedOfClient,
    moves: [...s.moves, { move, elapsedMs }],
    step: s.step + 1,
    lastMoveAt: now,
  };
  return { session, result: eng.result(activity, state) };
}

/**
 * Score a real-time attempt by replaying the client's input trace from the session seed. The run
 * must also have taken at least as long in real time as it claims to have taken in the simulation.
 */
export function replayMinigame(s: MinigameSession, trace: InputTrace, now: number): MinigameResult {
  assertGame(isRealtime(s.engine), 'INVALID', 'That game is played move by move.');
  const { activity } = lookup(s.venue, s.activity);
  const { result, ticks } = replay(createSim(s.engine as RealtimeEngine, activity, s.level, s.serverSeed), trace);
  assertGame(now - s.startedAt >= ticks * TICK_MS - balance.minigames.paceGraceMs, 'INVALID', 'That was quicker than the game itself.');
  return result;
}

/** XP multiplier for a skill: the Calling's affinity times the Discipline's mastery bonus. */
export function xpMultiplier(p: Pick<PlayerState, 'calling' | 'mastery'>, id: SkillId): number {
  const affinity = getCalling(p.calling).affinity.includes(id) ? 1 + balance.skills.affinityBonus : 1;
  return affinity * (1 + xpBonus(p, skill(id).discipline));
}

export function qualityFor(score: number, levelsAbove: number, rng: Rng): Quality {
  const m = balance.minigames;
  const q = clamp(score + clamp(levelsAbove * m.qualityPerLevel, 0, m.maxQualityBonus) + (rng.next() * 2 - 1) * m.qualityJitter, 0, 1);
  let tier = 0;
  m.qualityTiers.forEach((min, i) => {
    if (q >= min) tier = i;
  });
  return QUALITIES[tier]!;
}

/** A move as the session records it, with the real time the player took over it. */
export interface RecordedMove {
  move: MinigameMove;
  elapsedMs: number;
}

export interface Settlement {
  sessionId: string;
  venue: string;
  activity: string;
  skill: SkillId;
  score: number;
  title: string;
  text: string;
  breakdown: [string, number][];
  xp: number;
  levelBefore: number;
  level: number;
  renown: number;
  yield: SettledYield | null;
  coin: number;
  /** Feats done in the attempt, by the engine's names (see FEATS). */
  feats: string[];
  /** The best score at this activity before this attempt; null if it is the first on record. */
  best: number | null;
  /** Turn-based games reveal their seed and move list so the player can check the dice were fair. */
  reveal: { serverSeed: string; clientSeed: string | null; moves: unknown[] } | null;
}

/**
 * Pay out a finished attempt: skill XP (with Calling affinity), Renown, goods and winnings, all
 * ledgered, and clear the player's session pointer. `xp` is added to the skill row by the commit.
 */
export function settleMinigame(
  player: PlayerState,
  s: MinigameSession,
  result: MinigameResult,
  input: { skillXp: number; now: number; skills?: SkillXp },
): ActionResult<Settlement> & { xp: number } {
  assertGame(player.minigame?.sessionId === s.id, 'CONFLICT', 'That game is already over.');
  const { venue, activity } = lookup(s.venue, s.activity);
  const discipline = skill(venue.skill).discipline;
  const score = clamp(result.score, 0, 1);
  const tool = s.tool?.bonus ?? 0;
  const xp = Math.round(activityXp(activity) * (0.6 + score) * result.xpMult * xpMultiplier(player, venue.skill) * (1 + tool));
  const renown = Math.round(xp * balance.minigames.renownPerXp);
  const levelBefore = skillProgress(input.skillXp).level;
  const level = skillProgress(input.skillXp + xp).level;

  const ledger: LedgerDelta[] = [];
  let made: SettledYield | null = null;
  if (result.yield && result.yield.quantity > 0) {
    const quality =
      result.yield.quality ??
      (result.yield.graded ? qualityFor(score + qualityBonus(player, discipline) + tool / 2 + (result.yield.bonus ?? 0), levelBefore - activity.level, createRng(`${s.serverSeed}:quality`)) : balance.start.quality);
    made = { itemKey: result.yield.itemKey, quality, quantity: result.yield.quantity };
    ledger.push({ kind: 'item', itemKey: made.itemKey, quality, delta: made.quantity, reason: `minigame:${venue.key}`, counterparty: SYSTEM });
  }
  if (result.coin > 0) ledger.push({ kind: 'coin', account: 'purse', delta: result.coin, reason: `winnings:${venue.key}`, counterparty: `NPC:${venue.key}` });

  const skillName = skill(venue.skill).name;
  const goods = made ? ` ${made.quantity} × ${made.quality} ${getItem(made.itemKey).name.toLowerCase()}.` : '';
  const feats = [...new Set(result.feats ?? [])];
  const data: MinigameEventData = {
    venue: venue.key,
    activity: activity.key,
    engine: s.engine,
    score,
    xp,
    feats,
    coin: result.coin,
    stake: stakeOf(activity),
    // Only goods made here: a ware handed back keeps the quality it came with.
    masterwork: made?.quality === 'masterwork' && !result.yield?.quality && !!result.yield?.graded,
  };
  const events: GameEvent[] = [
    { type: 'minigame', text: `${venue.name}: ${result.title}.${goods} +${xp} ${skillName} XP.`, data: { ...data } },
    ...(level > levelBefore ? [{ type: 'skill-level', text: `Your ${skillName} is now level ${level}.`, data: { skill: venue.skill, level } }] : []),
  ];
  // With every skill's XP to hand, recount the mastery tiers (and catch up any the row missed).
  let mastery = player.mastery;
  if (input.skills) {
    mastery = raiseTiers(player.mastery, masteryTiers({ ...input.skills, [venue.skill]: input.skillXp + xp }));
    events.push(...masteryEvents(player.mastery, mastery));
  }
  return {
    player: { ...player, purse: player.purse + result.coin, renown: player.renown + renown, minigame: null, ...(mastery ? { mastery } : {}) },
    ledger,
    events,
    xp,
    outcome: {
      sessionId: s.id,
      venue: venue.key,
      activity: activity.key,
      skill: venue.skill,
      score,
      title: result.title,
      text: result.text,
      breakdown: result.breakdown,
      xp,
      levelBefore,
      level,
      renown,
      yield: made,
      coin: result.coin,
      feats,
      best: bestAt(player, venue.key, activity.key),
      reveal: isRealtime(s.engine) ? null : { serverSeed: s.serverSeed, clientSeed: s.clientSeed, moves: s.moves },
    },
  };
}

/** Walk away. Whatever was paid to sit down is gone. */
export function abandonMinigame(player: PlayerState, now: number): ActionResult<{ sessionId: string }> {
  const m = activeMinigame(player, now);
  assertGame(m, 'INVALID', 'You are not in the middle of anything.');
  const name = findVenue(m.venue)?.name ?? 'the venue';
  return {
    player: { ...player, minigame: null },
    ledger: [],
    events: [{ type: 'minigame-abandoned', text: `You walk away from ${name}.`, data: { venue: m.venue } }],
    outcome: { sessionId: m.sessionId },
  };
}

/**
 * Check a finished turn-based game: the revealed seed matches the commitment shown at the start,
 * and replaying the moves from it reproduces the final position.
 */
export function verifyReveal(input: {
  venue: string;
  activity: string;
  level: number;
  commitment: string;
  reveal: NonNullable<Settlement['reveal']>;
  ware?: Ware | null;
}): { ok: boolean; view: unknown } {
  const { engine, activity } = lookup(input.venue, input.activity);
  if (isRealtime(engine) || sha256Hex(input.reveal.serverSeed) !== input.commitment) return { ok: false, view: null };
  const eng = turnEngine(engine);
  let state = eng.init(activity, input.level, input.reveal.serverSeed, { ware: input.ware ?? null });
  const rng = createRng(`${input.reveal.serverSeed}:${input.reveal.clientSeed ?? ''}`);
  try {
    for (const { move, elapsedMs } of input.reveal.moves as RecordedMove[])
      state = eng.move(activity, state, move, { rng, elapsedMs, level: input.level, ware: input.ware ?? null });
  } catch {
    return { ok: false, view: null };
  }
  return { ok: eng.result(activity, state) !== null, view: eng.view(activity, state) };
}
